import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  ClipboardList, Plus, Search, Edit, Trash2, Printer, Clock, CheckCircle2,
  XCircle, AlertTriangle, ArrowRight, Send, ThumbsUp, Play, Check, Lock, Timer,
} from "lucide-react";
import { toast } from "sonner";
import { ExportButton } from "../components/shared/ExportButton";
import { WOAttachmentsButton } from "../components/shared/WOAttachments";
import { DropdownSelect, useDropdown } from "../components/shared/DropdownHooks";

function PriorityBadge({ priority }) {
  const { values } = useDropdown("wo_priorities");
  const match = values.find(v => v.value === priority);
  const color = match?.color || "#64748B";
  const label = match?.label || priority || "—";
  return <span className="px-2 py-0.5 text-xs font-bold tracking-wider" style={{ backgroundColor: `${color}15`, color }} data-testid={`priority-${priority}`}>{label.toUpperCase()}</span>;
}

function StatusBadge({ status }) {
  const { values } = useDropdown("wo_statuses");
  const match = values.find(v => v.value === status);
  const color = match?.color || "#64748B";
  const label = match?.label || status || "—";
  return <span className="px-2 py-0.5 text-xs font-bold tracking-wider" style={{ backgroundColor: `${color}15`, color }} data-testid={`status-${status}`}>{label.toUpperCase()}</span>;
}

function WorkOrderFormDialog({ open, onOpenChange, editing, formData, setFormData, onSubmit, machines, users }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-none" data-testid="create-wo-button"><Plus size={16} className="mr-2" />NEW WORK ORDER</Button>
      </DialogTrigger>
      <DialogContent className="bg-white rounded-none border border-[#E7E5E4] max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby="wo-form-desc">
        <DialogHeader><DialogTitle className="font-heading font-bold text-[#1C1917]">{editing ? "Edit Work Order" : "New Work Order"}</DialogTitle></DialogHeader>
        <div id="wo-form-desc" className="sr-only">Work order form</div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div><Label>Title *</Label><Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="mt-1 rounded-none border-[#E7E5E4]" data-testid="wo-title-input" /></div>
          <div><Label>Description</Label><Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="mt-1 rounded-none border-[#E7E5E4]" rows={4} data-testid="wo-description-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Priority *</Label>
              <DropdownSelect dropdownKey="wo_priorities" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-none border border-[#E7E5E4] bg-white text-sm" testId="wo-priority-select" />
            </div>
            <div>
              <Label>Target Date</Label>
              <Input type="date" value={formData.target_date} onChange={(e) => setFormData({ ...formData, target_date: e.target.value })} className="mt-1 rounded-none border-[#E7E5E4]" data-testid="wo-target-date" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Machine</Label>
              <select value={formData.machine_id} onChange={(e) => { const m = machines.find((x) => x.id === e.target.value); setFormData({ ...formData, machine_id: e.target.value, machine_number: m?.machine_number || "" }); }} className="mt-1 w-full h-10 px-3 rounded-none border border-[#E7E5E4] bg-white text-sm" data-testid="wo-machine-select">
                <option value="">Select Machine</option>
                {machines.map((m) => <option key={m.id} value={m.id}>{m.machine_number} — {m.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Assign To</Label>
              <select value={formData.assigned_to} onChange={(e) => { const u = users.find((x) => x.id === e.target.value); setFormData({ ...formData, assigned_to: e.target.value, assigned_to_name: u?.name || "" }); }} className="mt-1 w-full h-10 px-3 rounded-none border border-[#E7E5E4] bg-white text-sm" data-testid="wo-assign-select">
                <option value="">Unassigned</option>
                {users.filter((u) => u.role !== "hourly").map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
            </div>
          </div>
          <div><Label>Estimated Hours</Label><Input type="number" step="0.5" min="0" value={formData.estimated_hours || ""} onChange={(e) => setFormData({ ...formData, estimated_hours: e.target.value ? parseFloat(e.target.value) : null })} className="mt-1 rounded-none border-[#E7E5E4]" data-testid="wo-hours-input" /></div>
          <Button type="submit" className="w-full bg-[#D97706] hover:bg-[#B45309] text-white rounded-none" data-testid="wo-submit-button">{editing ? "UPDATE" : "CREATE WORK ORDER"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const INITIAL_FORM = { title: "", description: "", priority: "normal", machine_id: "", machine_number: "", assigned_to: "", assigned_to_name: "", estimated_hours: null, target_date: "" };

export default function WorkOrdersPage({ onLogout, userRole, isFeatureEnabled }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [machines, setMachines] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [laborWO, setLaborWO] = useState(null);
  const [laborHours, setLaborHours] = useState("");
  const [laborNotes, setLaborNotes] = useState("");
  const [detailWO, setDetailWO] = useState(null);

  const canEdit = userRole !== "hourly";
  const canApprove = userRole === "admin" || userRole === "superintendent";
  const canDelete = canApprove;

  const fetchOrders = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (statusFilter) params.set("status", statusFilter);
      if (priorityFilter) params.set("priority", priorityFilter);
      const resp = await axios.get(`${API}/workorders?${params}`, { headers: getAuthHeaders() });
      setOrders(resp.data);
    } catch { toast.error("Failed to load work orders"); }
    finally { setLoading(false); }
  }, [search, statusFilter, priorityFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/machines?limit=500`, { headers: getAuthHeaders() }),
      axios.get(`${API}/users`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
      axios.get(`${API}/workorders/stats/summary`, { headers: getAuthHeaders() }),
    ]).then(([mResp, uResp, sResp]) => {
      setMachines(mResp.data || []);
      setUsers(uResp.data || []);
      setStats(sResp.data);
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/workorders/${editing.id}`, formData, { headers: getAuthHeaders() });
        toast.success("Work order updated");
      } else {
        await axios.post(`${API}/workorders`, formData, { headers: getAuthHeaders() });
        toast.success("Work order created");
      }
      setDialogOpen(false);
      setEditing(null);
      setFormData(INITIAL_FORM);
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openEdit = (wo) => {
    setEditing(wo);
    setFormData({ title: wo.title, description: wo.description || "", priority: wo.priority, machine_id: wo.machine_id || "", machine_number: wo.machine_number || "", assigned_to: wo.assigned_to || "", assigned_to_name: wo.assigned_to_name || "", estimated_hours: wo.estimated_hours, target_date: wo.target_date || "" });
    setDialogOpen(true);
  };

  const transition = async (woId, action, params = "") => {
    try {
      const resp = await axios.post(`${API}/workorders/${woId}/${action}${params}`, {}, { headers: getAuthHeaders() });
      toast.success(resp.data.message);
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
  };

  const handleDelete = async (wo) => {
    if (!window.confirm(`Delete work order "${wo.title}"?`)) return;
    try {
      await axios.delete(`${API}/workorders/${wo.id}`, { headers: getAuthHeaders() });
      toast.success("Deleted");
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const handleLogLabor = async (e) => {
    e.preventDefault();
    if (!laborWO || !laborHours) return;
    try {
      const resp = await axios.post(`${API}/workorders/${laborWO.id}/labor?hours=${laborHours}&notes=${encodeURIComponent(laborNotes)}`, {}, { headers: getAuthHeaders() });
      toast.success(resp.data.message);
      setLaborWO(null);
      setLaborHours("");
      setLaborNotes("");
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openPrint = (wo) => { window.open(`${API}/workorders/${wo.id}/print`, "_blank"); };

  if (loading) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><div className="text-[#78716C] font-mono">Loading work orders...</div></div></Layout>;

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-4" data-testid="workorders-page">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-black text-4xl text-[#1C1917] mb-2" data-testid="wo-title">Work Orders</h1>
            <p className="text-[#78716C] text-sm">Create, track, and manage maintenance work orders</p>
          </div>
          <div className="flex gap-2">
            <ExportButton endpoint="/export/workorders" filename="workorders_export.csv" />
            {canEdit && <WorkOrderFormDialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditing(null); setFormData(INITIAL_FORM); } }} editing={editing} formData={formData} setFormData={setFormData} onSubmit={handleSubmit} machines={machines} users={users} />}
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="wo-stats">
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <div key={key} className="bg-white border border-[#E7E5E4] p-3 text-center cursor-pointer hover:border-[#1C1917] transition-colors" onClick={() => setStatusFilter(statusFilter === key ? "" : key)} data-testid={`stat-${key}`}>
                <div className="text-2xl font-mono font-black" style={{ color: cfg.color }}>{stats.by_status[key] || 0}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#78716C] font-bold">{cfg.label}</div>
              </div>
            ))}
            {stats.overdue > 0 && (
              <div className="bg-[#FEF2F2] border border-[#DC2626] p-3 text-center" data-testid="stat-overdue">
                <div className="text-2xl font-mono font-black text-[#DC2626]">{stats.overdue}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#DC2626] font-bold">Overdue</div>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-[#E7E5E4] p-4 flex flex-col sm:flex-row gap-3 items-end">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#78716C]" size={18} />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search work orders..." className="pl-10 rounded-none border-[#E7E5E4]" data-testid="wo-search" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 px-3 rounded-none border border-[#E7E5E4] bg-white text-sm" data-testid="wo-status-filter">
            <option value="">All Statuses</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="h-10 px-3 rounded-none border border-[#E7E5E4] bg-white text-sm" data-testid="wo-priority-filter">
            <option value="">All Priorities</option>
            {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>

        {/* List */}
        {orders.length === 0 ? (
          <div className="bg-white border border-[#E7E5E4] p-12 text-center">
            <ClipboardList size={48} className="mx-auto text-[#A8A29E] mb-4" />
            <p className="text-[#78716C]">{search || statusFilter || priorityFilter ? "No work orders match your filters" : "No work orders yet. Create your first one."}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((wo) => (
              <div key={wo.id} className="bg-white border border-[#E7E5E4] p-5 hover:border-[#1C1917] transition-colors" data-testid={`wo-card-${wo.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <PriorityBadge priority={wo.priority} />
                      <StatusBadge status={wo.status} />
                      <h3 className="font-heading font-bold text-[#1C1917] truncate">{wo.title}</h3>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#78716C] mt-2">
                      {wo.machine_number && <span>Machine: <strong className="text-[#1C1917] font-mono">{wo.machine_number}</strong></span>}
                      {wo.assigned_to_name && <span>Assigned: <strong className="text-[#1C1917]">{wo.assigned_to_name}</strong></span>}
                      {wo.target_date && <span>Target: <strong className="text-[#1C1917]">{wo.target_date}</strong></span>}
                      {wo.estimated_hours && <span>Est: <strong>{wo.estimated_hours}h</strong></span>}
                      {wo.actual_hours > 0 && <span>Actual: <strong className="text-[#D97706]">{wo.actual_hours}h</strong></span>}
                      <span>By: {wo.created_by_name}</span>
                    </div>
                    {wo.description && <p className="text-xs text-[#78716C] mt-2 line-clamp-2">{wo.description}</p>}
                    {wo.approval_notes && <p className="text-xs text-[#D97706] mt-1">Note: {wo.approval_notes}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {/* Workflow buttons */}
                    {canEdit && wo.status === "draft" && <Button size="sm" onClick={() => transition(wo.id, "submit")} className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-none" data-testid={`submit-wo-${wo.id}`}><Send size={14} className="mr-1" />Submit</Button>}
                    {canApprove && wo.status === "submitted" && (
                      <>
                        <Button size="sm" onClick={() => transition(wo.id, "approve")} className="bg-[#10B981] hover:bg-[#059669] text-white rounded-none" data-testid={`approve-wo-${wo.id}`}><ThumbsUp size={14} className="mr-1" />Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => transition(wo.id, "reject")} className="rounded-none border-[#DC2626] text-[#DC2626]" data-testid={`reject-wo-${wo.id}`}><XCircle size={14} /></Button>
                      </>
                    )}
                    {canEdit && wo.status === "approved" && <Button size="sm" onClick={() => transition(wo.id, "start")} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-none" data-testid={`start-wo-${wo.id}`}><Play size={14} className="mr-1" />Start</Button>}
                    {canEdit && wo.status === "in_progress" && <Button size="sm" onClick={() => transition(wo.id, "complete")} className="bg-[#059669] hover:bg-[#047857] text-white rounded-none" data-testid={`complete-wo-${wo.id}`}><Check size={14} className="mr-1" />Complete</Button>}
                    {canApprove && wo.status === "completed" && <Button size="sm" onClick={() => transition(wo.id, "close")} className="bg-[#44403C] hover:bg-[#1C1917] text-white rounded-none" data-testid={`close-wo-${wo.id}`}><Lock size={14} className="mr-1" />Close</Button>}
                    {/* Labor */}
                    {canEdit && (wo.status === "approved" || wo.status === "in_progress") && <Button size="sm" variant="outline" onClick={() => setLaborWO(wo)} className="rounded-none border-[#E7E5E4]" data-testid={`labor-wo-${wo.id}`}><Timer size={14} /></Button>}
                    {/* Edit (draft only) */}
                    {canEdit && wo.status === "draft" && <Button size="sm" variant="outline" onClick={() => openEdit(wo)} className="rounded-none border-[#E7E5E4]" data-testid={`edit-wo-${wo.id}`}><Edit size={14} /></Button>}
                    {/* Attachments (Photos & Voice) */}
                    <WOAttachmentsButton woId={wo.id} canEdit={canEdit} />
                    {/* Print */}
                    <Button size="sm" variant="outline" onClick={() => openPrint(wo)} className="rounded-none border-[#E7E5E4]" data-testid={`print-wo-${wo.id}`}><Printer size={14} /></Button>
                    {/* Delete */}
                    {canDelete && (wo.status === "draft" || wo.status === "closed") && <Button size="sm" variant="outline" onClick={() => handleDelete(wo)} className="rounded-none border-[#DC2626] text-[#DC2626]" data-testid={`delete-wo-${wo.id}`}><Trash2 size={14} /></Button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Labor Log Dialog */}
      <Dialog open={!!laborWO} onOpenChange={(o) => { if (!o) setLaborWO(null); }}>
        <DialogContent className="bg-white rounded-none border border-[#E7E5E4]" aria-describedby="labor-desc">
          <DialogHeader><DialogTitle className="font-heading font-bold text-[#1C1917]">Log Labor Hours</DialogTitle></DialogHeader>
          <div id="labor-desc" className="sr-only">Log hours worked</div>
          {laborWO && (
            <form onSubmit={handleLogLabor} className="space-y-4">
              <p className="text-sm text-[#78716C]">Work Order: <strong>{laborWO.title}</strong></p>
              {laborWO.labor_log?.length > 0 && (
                <div className="text-xs border border-[#E7E5E4] p-3 max-h-32 overflow-y-auto">
                  {laborWO.labor_log.map((e, i) => <div key={i} className="flex justify-between py-1 border-b border-[#F5F5F4] last:border-0"><span>{e.user_name}</span><span className="font-mono">{e.hours}h</span><span className="text-[#78716C]">{e.logged_at?.slice(0, 10)}</span></div>)}
                  <div className="font-bold mt-1 pt-1 border-t border-[#1C1917]">Total: {laborWO.actual_hours || 0}h</div>
                </div>
              )}
              <div><Label>Hours *</Label><Input type="number" step="0.25" min="0.25" value={laborHours} onChange={(e) => setLaborHours(e.target.value)} required className="mt-1 rounded-none border-[#E7E5E4]" data-testid="labor-hours-input" /></div>
              <div><Label>Notes</Label><Input value={laborNotes} onChange={(e) => setLaborNotes(e.target.value)} className="mt-1 rounded-none border-[#E7E5E4]" placeholder="Work performed..." data-testid="labor-notes-input" /></div>
              <Button type="submit" className="w-full bg-[#D97706] hover:bg-[#B45309] text-white rounded-none" data-testid="labor-submit">LOG HOURS</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
